import React from "react";
import ExplanationBlock from "./ExplanationBlock";

const YEARLY_HEADERS = [
  "Year",
  "Annual Threshold (£)",
  "Annual Income (£)",
  "Annual Interest Rate (%)",
  "Open (£)",
  "Interest (+\u2060£)",
  "Payment (-\u2060£)",
  "Close (£)",
];

function YearlyBreakdownTable({
  table,
  planKey,
  expandedRows,
  toggleExpand,
  editedIncomes,
  setEditedIncomes,
  handleIncomeEdit,
  expandedMonthExplanations,
  toggleMonthExplain,
  onResetIncomes,
}) {
  if (!table || table.length === 0) return null;

  return (
    <div className="table-container">
      <table className="styled-table">
        <thead>
          <tr>
            {YEARLY_HEADERS.map((header) =>
              header === "Annual Income (£)" ? (
                <th key={header} style={{ position: "relative" }}>
                  Annual Income (£)
                  <span
                    className="info-btn"
                    tabIndex={0}
                    aria-label="How annual income editing works"
                  >
                    <span className="info-icon">i</span>
                    <span className="info-tooltip info-tooltip-below-left">
                      Manually modifying the income below and clicking away will cause instant updating. For all other input changes please click 'Calculate' for the update to take place.
                    </span>
                  </span>
                  <button
                    className="income-reset-btn"
                    title="Reset all incomes"
                    onClick={(e) => {
                      e.stopPropagation();
                      onResetIncomes();
                    }}
                    tabIndex={-1}
                  >
                    Reset
                  </button>
                </th>
              ) : (
                <th key={header}>{header}</th>
              )
            )}
            <th>
              Explain
              <span className="info-btn" tabIndex={0} aria-label="How explain works">
                <span className="info-icon">i</span>
                <span className="info-tooltip info-tooltip-below-left">View month by month.</span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {table.map((row, idx) => (
            <React.Fragment key={idx}>
              <tr>
                <td>{row.year}</td>
                <td>{row.threshold.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    className={`income-input${idx === 0 ? " first-row" : ""}`}
                    value={
                      editedIncomes[`year-${row.year}`] !== undefined
                        ? editedIncomes[`year-${row.year}`]
                        : Number(row.income).toFixed(2)
                    }
                    onChange={(e) => {
                      if (idx === 0) return;
                      const val = e.target.value;
                      setEditedIncomes((prev) => ({
                        ...prev,
                        [`year-${row.year}`]: val,
                      }));
                    }}
                    onBlur={() => {
                      if (idx === 0) return;
                      let val = editedIncomes[`year-${row.year}`];
                      if (val === "" || val === undefined) val = "0.00";
                      else val = parseFloat(val).toFixed(2);

                      const updated = { ...editedIncomes, [`year-${row.year}`]: val };
                      setEditedIncomes(updated);
                      handleIncomeEdit(updated);
                    }}
                    onKeyDown={(e) => {
                      if (idx === 0) return;
                      if (e.key === "Enter") {
                        let val = editedIncomes[`year-${row.year}`];
                        if (val === "" || val === undefined) val = "0.00";
                        else val = parseFloat(val).toFixed(2);

                        const updated = { ...editedIncomes, [`year-${row.year}`]: val };
                        setEditedIncomes(updated);
                        handleIncomeEdit(updated);
                      }
                    }}
                    style={{ width: 120 }}
                    readOnly={idx === 0}
                  />
                </td>
                <td>{row.interest_rate.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                <td>{row.open.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td>{row.interest.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td>{row.payment.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td>{row.close.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td>
                  <button className="expand-btn" onClick={() => toggleExpand(planKey, idx)}>
                    {expandedRows[planKey]?.[idx] ? "Hide" : "Explain"}
                  </button>
                </td>
              </tr>
              {expandedRows[planKey]?.[idx] && row.months && (
                <tr>
                  <td colSpan={YEARLY_HEADERS.length + 1}>
                    <div className="monthly-breakdown-container">
                      <table className="styled-table">
                        <thead>
                          <tr>
                            <th>Month</th>
                            <th>Annual Threshold (£)</th>
                            <th>Income (£)</th>
                            <th>Annual Interest Rate (%)</th>
                            <th>Open (£)</th>
                            <th>{"Interest (+\u2060£)"}</th>
                            <th>{"Payment (-\u2060£)"}</th>
                            <th>Close (£)</th>
                            <th>
                              Explain
                              <span className="info-btn" tabIndex={0} aria-label="How explain works">
                                <span className="info-icon">i</span>
                                <span className="info-tooltip info-tooltip-below-left">View workings for calculations.</span>
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.months.map((month, monthIdx) => {
                            const explanationKey = `${planKey}-${idx}-${monthIdx}`;
                            return (
                              <React.Fragment key={monthIdx}>
                                <tr>
                                  <td>{month.month}</td>
                                  <td>{month.threshold.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td>{Number(month.income).toFixed(2).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td>{month.interest_rate.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                  <td>{month.open.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td>{month.interest.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td>{month.payment.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td>{month.close.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td>
                                    <button
                                      className="expand-btn"
                                      onClick={() => toggleMonthExplain(planKey, idx, monthIdx)}
                                    >
                                      {expandedMonthExplanations[explanationKey] ? "Hide" : "Explain"}
                                    </button>
                                  </td>
                                </tr>
                                {expandedMonthExplanations[explanationKey] && (
                                  <tr>
                                    <td colSpan={9}>
                                      <ExplanationBlock text={month.explanation} />
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default YearlyBreakdownTable;
